import { operatingSlots, type VenueSlot } from '@/shared/time/venueSlots';
import type { VenueWeekdayHours } from '@/modules/venues/operatingHours';

export type VenueOperatingDayState = 'unconfigured' | 'closed' | 'open';

export type VenueOperatingWindowVenue = Readonly<{
  timezone: string;
  slotGranularityMinutes: number;
  operatingHoursConfigured: boolean;
  weekdayHours: readonly VenueWeekdayHours[];
}>;

export type OfferableStartTimesOptions = Readonly<{
  excludePast?: boolean;
  now?: Date;
}>;

export type VenueOperatingWindow = Readonly<{
  dayState(date: string): VenueOperatingDayState;
  offerableStartTimes(
    date: string,
    sessionDurationMinutes: number,
    options?: OfferableStartTimesOptions,
  ): readonly string[];
  offerableSlots(
    date: string,
    duration: number,
    options?: OfferableStartTimesOptions,
  ): readonly VenueSlot[];
  contains(
    date: string,
    startTime: string,
    sessionDurationMinutes: number,
    occurrence?: number,
  ): boolean;
}>;

const MINUTES_PER_DAY = 24 * 60;

type Weekday = VenueWeekdayHours['weekday'];

type OperatingInterval = Readonly<{
  openMinutes: number;
  closeMinutes: number;
}>;

type ResolvedDay = Readonly<{
  state: VenueOperatingDayState;
  interval: OperatingInterval | null;
}>;

const WEEKDAY_BY_INDEX: readonly Weekday[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

function weekdayFromIsoDate(date: string): Weekday {
  const [year, month, day] = date.split('-').map(Number);
  const weekdayIndex = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, 12)).getUTCDay();
  return WEEKDAY_BY_INDEX[weekdayIndex] ?? 'MONDAY';
}

function normalizeTime(time: string): string {
  return time.slice(0, 5);
}

function minutesSinceMidnight(time: string): number {
  const normalized = normalizeTime(time);
  const [hours = '0', minutes = '0'] = normalized.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function formatTime(minutesSinceMidnight: number): string {
  const hours = String(Math.floor(minutesSinceMidnight / 60)).padStart(2, '0');
  const minutes = String(minutesSinceMidnight % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function venueTodayIsoDate(timezone: string, now: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function venueLocalMinutes(timezone: string, now: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: 'numeric',
    minute: 'numeric',
  }).formatToParts(now);
  const hours = Number(parts.find((part) => part.type === 'hour')?.value ?? '0') % 24;
  const minutes = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
  return hours * 60 + minutes;
}

function minimumStartMinutes(
  venue: VenueOperatingWindowVenue,
  date: string,
  options: OfferableStartTimesOptions,
): number {
  if (options.excludePast !== true) {
    return Number.NEGATIVE_INFINITY;
  }

  const now = options.now ?? new Date();
  if (date !== venueTodayIsoDate(venue.timezone, now)) {
    return Number.NEGATIVE_INFINITY;
  }

  return venueLocalMinutes(venue.timezone, now);
}

function resolveInterval(
  weekdayHoursByWeekday: ReadonlyMap<Weekday, VenueWeekdayHours>,
  weekday: Weekday,
): OperatingInterval | null {
  const weekdayHours = weekdayHoursByWeekday.get(weekday);
  if (!weekdayHours?.openTime || !weekdayHours.closeTime) return null;
  return {
    openMinutes: minutesSinceMidnight(weekdayHours.openTime),
    // A 00:00 close is midnight at the end of that day, not the start of it (ADR 0080).
    closeMinutes: minutesSinceMidnight(weekdayHours.closeTime) || MINUTES_PER_DAY,
  };
}

function resolveDay(
  venue: VenueOperatingWindowVenue,
  weekdayHoursByWeekday: ReadonlyMap<Weekday, VenueWeekdayHours>,
  date: string,
): ResolvedDay {
  if (!venue.operatingHoursConfigured) {
    return { state: 'unconfigured', interval: null };
  }

  const interval = resolveInterval(weekdayHoursByWeekday, weekdayFromIsoDate(date));
  if (interval == null) {
    return { state: 'closed', interval: null };
  }

  return { state: 'open', interval };
}

export function createVenueOperatingWindow(venue: VenueOperatingWindowVenue): VenueOperatingWindow {
  const weekdayHoursByWeekday = new Map(
    venue.weekdayHours.map((entry) => [entry.weekday, entry] as const),
  );

  return {
    dayState(date) {
      return resolveDay(venue, weekdayHoursByWeekday, date).state;
    },
    offerableStartTimes(date, sessionDurationMinutes, options = {}) {
      const { interval } = resolveDay(venue, weekdayHoursByWeekday, date);
      if (interval == null) return [];

      const minStartMinutes = minimumStartMinutes(venue, date, options);
      const startTimes: string[] = [];
      for (
        let startMinutes = interval.openMinutes;
        startMinutes + sessionDurationMinutes <= interval.closeMinutes;
        startMinutes += venue.slotGranularityMinutes
      ) {
        if (startMinutes < minStartMinutes) continue;
        startTimes.push(formatTime(startMinutes));
      }
      return startTimes;
    },
    offerableSlots(date, duration, options = {}) {
      const { interval } = resolveDay(venue, weekdayHoursByWeekday, date);
      if (interval == null) return [];
      return operatingSlots(
        date,
        formatTime(interval.openMinutes),
        formatTime(interval.closeMinutes),
        venue.timezone,
        venue.slotGranularityMinutes,
        duration,
      ).filter(
        (slot) =>
          options.excludePast !== true || slot.startsAt >= (options.now ?? new Date()).getTime(),
      );
    },
    contains(date, startTime, sessionDurationMinutes, occurrence) {
      return this.offerableSlots(date, sessionDurationMinutes).some(
        (slot) =>
          slot.time === startTime.slice(0, 5) && (slot.occurrence ?? 1) === (occurrence ?? 1),
      );
    },
  };
}
